import { useDataProvider, useGetIdentity, useTranslate } from "ra-core";
import { useCallback, useMemo } from "react";

import type { Tag } from "../types";
import { createEachRow } from "../dataImport/createEachRow";
import { fetchRecordsWithCache } from "../dataImport/fetchRecordsWithCache";
import { toText } from "../dataImport/parseCell";
import type { ImportRow, ProcessImportBatch } from "../dataImport/types";
import { toLeadScore } from "../dataImport/useCompanyImport";
import { useCompanyResolver } from "../dataImport/useCompanyResolver";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { toBoolean, toChoice, toDay, toList } from "../spreadsheet/cells";
import { readCustomFields } from "../spreadsheet/customFieldColumns";
import {
  DEFAULT_CONTACT_STATUS,
  contactGender,
  translateContactGenderLabel,
} from "./contactModel";

/**
 * Creates a contact per row of an Excel or CSV file. Companies named in the
 * `company` and `other_companies` columns are reused, or created when unknown;
 * tags likewise. List columns take the label shown in the CRM or the stored value.
 */
export function useContactImport(): ProcessImportBatch {
  const user = useGetIdentity();
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const { noteStatuses, customFields } = useConfigurationContext();

  const getCompanies = useCompanyResolver();

  // Tags cache to avoid creating the same tag multiple times and costly roundtrips
  // Cache is dependent of dataProvider, so it's safe to use it as a dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tagsCache = useMemo(() => new Map<string, Tag>(), [dataProvider]);
  const getTags = useCallback(
    async (names: string[]) =>
      fetchRecordsWithCache<Tag>(
        "tags",
        tagsCache,
        names,
        (name) => ({
          name,
          color: "#f9f9f9",
        }),
        dataProvider,
      ),
    [tagsCache, dataProvider],
  );

  return useCallback(
    async (batch: ImportRow[]) => {
      const today = new Date().toISOString();
      const genderOptions = contactGender.map((gender) => ({
        value: gender.value,
        label: translateContactGenderLabel(gender, translate),
      }));
      const companyNamesOf = (row: ImportRow) =>
        [toText(row.company), ...toList(row.other_companies)].filter(
          (name): name is string => !!name,
        );
      const [companies, tags] = await Promise.all([
        getCompanies(batch.flatMap(companyNamesOf)),
        getTags(batch.flatMap((row) => toList(row.tags))),
      ]);

      return createEachRow(
        batch.map((row) => {
          const email_jsonb = [
            { email: toText(row.email_work), type: "Work" },
            { email: toText(row.email_home), type: "Home" },
            { email: toText(row.email_other), type: "Other" },
          ].filter(({ email }) => email);
          const phone_jsonb = [
            { number: toText(row.phone_work), type: "Work" },
            { number: toText(row.phone_home), type: "Home" },
            { number: toText(row.phone_other), type: "Other" },
          ].filter(({ number }) => number);
          const companyIds = companyNamesOf(row)
            .map((name) => companies.get(name)?.id)
            .filter((id) => id != null);
          const mainCompanyName = toText(row.company);
          const firstSeen = toDay(row.first_seen);
          const lastSeen = toDay(row.last_seen);

          return dataProvider.create("contacts", {
            data: {
              first_name: toText(row.first_name),
              last_name: toText(row.last_name),
              gender: toChoice(row.gender, genderOptions),
              title: toText(row.title),
              email_jsonb,
              phone_jsonb,
              background: toText(row.background),
              first_seen: firstSeen ? `${firstSeen}T00:00:00.000Z` : today,
              last_seen: lastSeen ? `${lastSeen}T00:00:00.000Z` : today,
              has_newsletter: toBoolean(row.has_newsletter) ?? false,
              status:
                toChoice(row.status, noteStatuses) ?? DEFAULT_CONTACT_STATUS,
              lead_score: toLeadScore(row.lead_score),
              company_id: mainCompanyName
                ? companies.get(mainCompanyName)?.id
                : companyIds[0],
              company_ids: companyIds,
              tags: toList(row.tags)
                .map((name) => tags.get(name)?.id)
                .filter((id) => id != null),
              linkedin_url: toText(row.linkedin_url),
              custom_fields: readCustomFields(row, customFields.contacts ?? []),
              sales_id: user?.identity?.id,
            },
          });
        }),
      );
    },
    [
      customFields,
      dataProvider,
      getCompanies,
      getTags,
      noteStatuses,
      translate,
      user?.identity?.id,
    ],
  );
}
